import {
  createSerializer,
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  parseAsIsoDate,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';
import type { AllocationFilters, AllocationSortField, SortDirection } from '../types/Filters';
import { toApiDate } from '../utils/data';
import {
  allFiltersCleared,
  hasAnyFilter,
  idsOrNull,
  SORT_DIRECTIONS,
  selectionFrom,
  selectionsFrom,
  toPercentageBound,
} from '../utils/filters';

export type SelectedOption = { id: number; name: string };

const SORT_FIELDS = [
  'professional',
  'project',
  'contract',
  'client',
  'allocatedPercentage',
  'startDate',
  'endDate',
] as const satisfies readonly AllocationSortField[];

/** Range de meses da linha do tempo (meses visíveis quando não há Início+Fim nos filtros). */
export const TIMELINE_SPANS = ['3', '6', '12'] as const;
export type TimelineSpan = (typeof TIMELINE_SPANS)[number];

/** Status filtráveis na linha do tempo — permite vários ao mesmo tempo (ex. futuras + encerradas). */
export const TIMELINE_STATUSES = ['active', 'upcoming', 'closed'] as const;
export type TimelineStatus = (typeof TIMELINE_STATUSES)[number];

const PARSERS = {
  projectSearch: parseAsString,
  contractSearch: parseAsString,
  clientSearch: parseAsString,
  accountDirectorSearch: parseAsString,
  operationManagerSearch: parseAsString,
  projectManagerSearch: parseAsString,
  tagIds: parseAsArrayOf(parseAsInteger),
  tagNames: parseAsArrayOf(parseAsString),
  billable: parseAsBoolean,
  minPercentage: parseAsInteger,
  maxPercentage: parseAsInteger,
  professionalIds: parseAsArrayOf(parseAsInteger),
  professionalNames: parseAsArrayOf(parseAsString),
  projectId: parseAsInteger,
  projectName: parseAsString,
  contractId: parseAsInteger,
  contractName: parseAsString,
  clientId: parseAsInteger,
  clientName: parseAsString,
  notes: parseAsString,
  startDate: parseAsIsoDate,
  endDate: parseAsIsoDate,
  sortBy: parseAsStringLiteral(SORT_FIELDS),
  sortDirection: parseAsStringLiteral(SORT_DIRECTIONS),
  page: parseAsInteger.withDefault(1),
  perPage: parseAsInteger.withDefault(30),
  timelinePage: parseAsInteger.withDefault(1),
  timelineSpan: parseAsStringLiteral(TIMELINE_SPANS).withDefault('3'),
  timelineStatus: parseAsArrayOf(parseAsStringLiteral(TIMELINE_STATUSES)),
};

/** Filtros valem pras duas visões, então qualquer escrita volta as duas à página 1. */
const FIRST_PAGES = { page: 1, timelinePage: 1 } as const;

const serialize = createSerializer(PARSERS);

export type ColumnSearchKey =
  | 'projectSearch'
  | 'contractSearch'
  | 'clientSearch'
  | 'accountDirectorSearch'
  | 'operationManagerSearch'
  | 'projectManagerSearch';

export type PercentageRange = { min: number | null; max: number | null };

/** Um filtro removível pelo chip — a faixa de % e o período saem inteiros, não meia faixa. */
export type FilterKey =
  | ColumnSearchKey
  | 'tags'
  | 'billable'
  | 'percentage'
  | 'professional'
  | 'project'
  | 'contract'
  | 'client'
  | 'notes'
  | 'period';

/**
 * Rascunho do painel "Filtros avançados", comitado de uma vez no "Aplicar".
 * O painel só mostra os campos das colunas visíveis, mas o rascunho leva todos:
 * filtro de coluna escondida segue aplicado (e no chip).
 */
export type AdvancedFilterValues = {
  professionals: SelectedOption[];
  project: SelectedOption | null;
  contract: SelectedOption | null;
  client: SelectedOption | null;
  percentage: PercentageRange;
  billable: boolean | null;
  notes: string;
  tags: SelectedOption[];
  accountDirectorSearch: string;
  operationManagerSearch: string;
  projectManagerSearch: string;
  startDate: Date | null;
  endDate: Date | null;
};

const CLEARED_BY_KEY = {
  projectSearch: { projectSearch: null },
  contractSearch: { contractSearch: null },
  clientSearch: { clientSearch: null },
  accountDirectorSearch: { accountDirectorSearch: null },
  operationManagerSearch: { operationManagerSearch: null },
  projectManagerSearch: { projectManagerSearch: null },
  tags: { tagIds: null, tagNames: null },
  billable: { billable: null },
  percentage: { minPercentage: null, maxPercentage: null },
  professional: { professionalIds: null, professionalNames: null },
  project: { projectId: null, projectName: null },
  contract: { contractId: null, contractName: null },
  client: { clientId: null, clientName: null },
  notes: { notes: null },
  period: { startDate: null, endDate: null },
} as const satisfies Record<FilterKey, object>;

const ALL_FILTERS_CLEARED = allFiltersCleared(CLEARED_BY_KEY);

/** Guarda o nome ao lado do id, como Profissional: o chip não espera a lista de tags. */
const tagsState = (selected: SelectedOption[]) => ({
  tagIds: idsOrNull(selected.map((option) => option.id)),
  tagNames: selected.length > 0 ? selected.map((option) => option.name) : null,
});

const percentageState = (range: PercentageRange) => ({
  minPercentage: toPercentageBound(range.min),
  maxPercentage: toPercentageBound(range.max),
});

/**
 * Filtros, ordenação e página vivem na URL via `nuqs` (`07-spec-front.md` §4).
 * Tabela e linha do tempo paginam cada uma por si (`page` e `timelinePage`);
 * toda escrita de filtro volta as duas para 1. Profissionais (vários),
 * Projeto, Contrato e Cliente guardam o rótulo ao lado do id — evita um
 * round-trip pra resolver o rótulo do combobox e do chip ao recarregar a
 * página com o filtro aplicado (link compartilhado).
 */
export function useAllocationFilters() {
  const [state, setState] = useQueryStates(PARSERS, { throttleMs: 300 });

  const filters: AllocationFilters = {
    projectSearch: state.projectSearch ?? undefined,
    contractSearch: state.contractSearch ?? undefined,
    clientSearch: state.clientSearch ?? undefined,
    accountDirectorSearch: state.accountDirectorSearch ?? undefined,
    operationManagerSearch: state.operationManagerSearch ?? undefined,
    projectManagerSearch: state.projectManagerSearch ?? undefined,
    tagIds: state.tagIds ?? undefined,
    billable: state.billable ?? undefined,
    minPercentage: state.minPercentage ?? undefined,
    maxPercentage: state.maxPercentage ?? undefined,
    professionalIds: state.professionalIds ?? undefined,
    projectId: state.projectId ?? undefined,
    contractId: state.contractId ?? undefined,
    clientId: state.clientId ?? undefined,
    notes: state.notes ?? undefined,
    startDate: toApiDate(state.startDate) ?? undefined,
    endDate: toApiDate(state.endDate) ?? undefined,
    sortBy: state.sortBy ?? undefined,
    sortDirection: state.sortBy ? (state.sortDirection ?? 'asc') : undefined,
    page: state.page,
    perPage: state.perPage !== 30 ? state.perPage : undefined,
  };

  const professionals = selectionsFrom(state.professionalIds, state.professionalNames);
  const tags = selectionsFrom(state.tagIds, state.tagNames);
  const project = selectionFrom(state.projectId, state.projectName);
  const contract = selectionFrom(state.contractId, state.contractName);
  const client = selectionFrom(state.clientId, state.clientName);

  const hasActiveFilters = hasAnyFilter(CLEARED_BY_KEY, state);

  return {
    filters,
    professionals,
    tags,
    project,
    contract,
    client,
    startDate: state.startDate,
    endDate: state.endDate,
    setColumnSearch: (key: ColumnSearchKey, value: string) =>
      setState({ [key]: value.trim() || null, ...FIRST_PAGES }),
    setTags: (selected: SelectedOption[]) => setState({ ...tagsState(selected), ...FIRST_PAGES }),
    setBillable: (billable: boolean | null) => setState({ billable, ...FIRST_PAGES }),
    setPercentageRange: (range: PercentageRange) =>
      setState({ ...percentageState(range), ...FIRST_PAGES }),
    /** `'none'` (terceiro clique no cabeçalho) volta à ordem padrão (início da alocação). */
    setSort: (field: AllocationSortField, direction: SortDirection | 'none') =>
      setState(
        direction === 'none'
          ? { sortBy: null, sortDirection: null }
          : { sortBy: field, sortDirection: direction },
      ),
    applyAdvanced: (values: AdvancedFilterValues) =>
      setState({
        professionalIds: idsOrNull(values.professionals.map((option) => option.id)),
        professionalNames:
          values.professionals.length > 0
            ? values.professionals.map((option) => option.name)
            : null,
        projectId: values.project?.id ?? null,
        projectName: values.project?.name ?? null,
        contractId: values.contract?.id ?? null,
        contractName: values.contract?.name ?? null,
        clientId: values.client?.id ?? null,
        clientName: values.client?.name ?? null,
        ...percentageState(values.percentage),
        billable: values.billable,
        notes: values.notes.trim() || null,
        ...tagsState(values.tags),
        accountDirectorSearch: values.accountDirectorSearch.trim() || null,
        operationManagerSearch: values.operationManagerSearch.trim() || null,
        projectManagerSearch: values.projectManagerSearch.trim() || null,
        startDate: values.startDate,
        endDate: values.endDate,
        ...FIRST_PAGES,
      }),
    clearFilter: (key: FilterKey) => setState({ ...CLEARED_BY_KEY[key], ...FIRST_PAGES }),
    timelinePage: state.timelinePage,
    perPage: state.perPage,
    setPerPage: (perPage: number) => setState({ perPage, ...FIRST_PAGES }),
    goToPage: (page: number) => setState({ page }),
    goToTimelinePage: (timelinePage: number) => setState({ timelinePage }),
    timelineSpan: state.timelineSpan,
    /** Mudar o range muda a janela (dados buscados), mas NÃO reseta a paginação por profissional. */
    setTimelineSpan: (timelineSpan: TimelineSpan) => setState({ timelineSpan }),
    /** Status selecionados na timeline (vazio = todos). Só reseta a página da timeline. */
    timelineStatus: state.timelineStatus ?? [...TIMELINE_STATUSES],
    setTimelineStatus: (statuses: TimelineStatus[]) => {
      const all = statuses.length === 0 || statuses.length === TIMELINE_STATUSES.length;
      setState({ timelineStatus: all ? null : statuses, timelinePage: 1 });
    },
    /** Do estado, não de `location.search`: o `nuqs` grava a URL por fora do roteador. */
    searchUrlFor: (pathname: string) =>
      serialize(pathname, { ...state, page: null, timelinePage: null }),
    /** Limpa os filtros, não a ordenação. */
    clearFilters: () => setState({ ...ALL_FILTERS_CLEARED, ...FIRST_PAGES }),
    hasActiveFilters,
  };
}

export type AllocationFilterState = ReturnType<typeof useAllocationFilters>;
